/**
 * CRO scorecard route — sectioned tile grid.
 *
 * Sections:
 *   PIPELINE COVERAGE: CRO-02, CRO-03, CRO-04, CRO-05 funnel, Opps Created (90d)
 *   REVENUE & DEAL MIX: CRO-01 (Closed Won New ARR pacing), CRO-08, CRO-09, MAIN-12
 *   FORECAST: CRO-10 (Forecast vs. Budget) — CRO-11 variance removed 2026-07-10
 *   AE ACTIVITY: CRO-06, CRO-07 (trend), MAIN-04 Outbound Activity
 *
 * All big-number/trend queries fire in parallel on mount via TanStack Query.
 * The three ported/new tiles pull from separate endpoints:
 *   - MAIN-04 / MAIN-12 from GET /api/main/scorecard (useMainScorecard)
 *   - Opps Created from GET /api/cro/opps-created (useCroOppsCreated)
 *
 * Freshness reads from the first successful response — CRO metrics share the
 * same 7-min cron cycle.
 */
import { useEffect } from 'react';


import { AppShell } from '../components/AppShell.js';
import { CroArrPacingTile } from '../components/CroArrPacingTile.js';
import { CroFunnelPacingTile } from '../components/CroFunnelPacingTile.js';
import { CroOppsCreatedTile } from '../components/CroOppsCreatedTile.js';
import { CroTile } from '../components/CroTile.js';
import { CroTrendTile } from '../components/CroTrendTile.js';
import { FreshnessPill } from '../components/FreshnessPill.js';
import { MainBucketedTile } from '../components/main/MainBucketedTile.js';
import { getMetricDefinition } from '../lib/definitions.js';
import { useCroArrPacing } from '../queries/useCroArrPacing.js';
import { useCroFunnelPacing } from '../queries/useCroFunnelPacing.js';
import { useCroOppsCreated } from '../queries/useCroOppsCreated.js';
import { useCroScorecard } from '../queries/useCroScorecard.js';
import { useCroTrend } from '../queries/useCroTrend.js';
import { useMainScorecard } from '../queries/useMainScorecard.js';

import type { FreshnessPill as FreshnessPillData } from '@saledash/shared';

const DEFAULT_FRESHNESS: FreshnessPillData = {
  status: 'live',
  lastRefreshedAt: new Date().toISOString(),
};

const SECTION_HEADER = (
  id: string,
  label: string,
): React.JSX.Element => (
  <div className="mb-3 flex items-center gap-3">
    <h2 id={id} className="section-label shrink-0">
      {label}
    </h2>
    <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-ink-200 to-transparent" />
  </div>
);

export function CroScorecardPage(): React.JSX.Element {
  useEffect(() => {
    const prev = document.title;
    document.title = 'CRO Scorecard · Adeptia';
    return () => {
      document.title = prev;
    };
  }, []);

  const cro01 = useCroArrPacing();
  const cro02 = useCroScorecard('CRO-02');
  const cro03 = useCroScorecard('CRO-03');
  const cro04 = useCroScorecard('CRO-04');
  const cro05 = useCroFunnelPacing();
  const cro08 = useCroScorecard('CRO-08');
  const cro09 = useCroScorecard('CRO-09');
  const cro10 = useCroScorecard('CRO-10');
  const cro06 = useCroTrend('CRO-06');
  const cro07 = useCroTrend('CRO-07');
  const oppsCreated = useCroOppsCreated();

  // Ported main-dashboard tiles (Outbound Activity, Closed Won Avg) come from the
  // shared /api/main/scorecard payload — same logic as the /main page.
  const { data: main, isLoading: mainLoading } = useMainScorecard();

  const freshness: FreshnessPillData =
    cro01.data?.freshness ??
    cro02.data?.freshness ??
    cro03.data?.freshness ??
    cro06.data?.freshness ??
    DEFAULT_FRESHNESS;

  /** Render a ported main-dashboard bucketed tile (same wiring as MainTileSection). */
  function renderMainBucketed(
    metricId: string,
    chartType: 'weeklyBar' | 'lineByQuarter' | 'stackedBar',
  ): React.JSX.Element {
    let title = metricId;
    let tooltipFormula: string | undefined;
    try {
      const def = getMetricDefinition(metricId);
      title = def.name;
      tooltipFormula = def.formula_plain_english;
    } catch {
      // metricId not in definitions.json — fall back to the raw ID.
    }
    return (
      <MainBucketedTile
        metricId={metricId}
        title={title}
        // Conditional spread keeps exactOptionalPropertyTypes happy (never pass
        // `tooltipFormula={undefined}`).
        {...(tooltipFormula ? { tooltipFormula } : {})}
        chartType={chartType}
        data={main?.grouped[metricId]}
        snapshots={main?.snap[metricId]?.snapshots ?? []}
        isLoading={mainLoading}
        isError={false}
        error={null}
        refetch={() => undefined}
      />
    );
  }

  return (
    <AppShell
      brandSuffix="CRO View"
      freshnessSlot={<FreshnessPill freshness={freshness} />}
      crossLink={{ label: 'Main scorecard ↗', to: '/main' }}
    >
      <div className="space-y-6">
        {/* Section 1: PIPELINE COVERAGE */}
        <section aria-labelledby="section-pipeline">
          {SECTION_HEADER('section-pipeline', 'Pipeline Coverage')}
          <div
            data-cro-tile-grid
            className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3"
          >
            <CroTile
              metricId="CRO-02"
              data={cro02.data}
              isLoading={cro02.isLoading}
              isError={cro02.isError}
              error={cro02.error}
              refetch={() => { void cro02.refetch(); }}
            />
            <CroTile
              metricId="CRO-03"
              data={cro03.data}
              isLoading={cro03.isLoading}
              isError={cro03.isError}
              error={cro03.error}
              refetch={() => { void cro03.refetch(); }}
            />
            <CroTile
              metricId="CRO-04"
              data={cro04.data}
              isLoading={cro04.isLoading}
              isError={cro04.isError}
              error={cro04.error}
              refetch={() => { void cro04.refetch(); }}
            />
          </div>

          {/* CRO-05 Total Pipeline Funnel — cumulative trend, full-width. */}
          <div className="mt-3">
            <CroFunnelPacingTile
              data={cro05.data}
              isLoading={cro05.isLoading}
              isError={cro05.isError}
              error={cro05.error}
              refetch={() => { void cro05.refetch(); }}
            />
          </div>

          {/* Opps Created (trailing 90d) — count + stage mix + $ created, WoW/MoM. */}
          <div className="mt-3">
            <CroOppsCreatedTile
              data={oppsCreated.data}
              isLoading={oppsCreated.isLoading}
              isError={oppsCreated.isError}
              error={oppsCreated.error}
              refetch={() => { void oppsCreated.refetch(); }}
            />
          </div>
        </section>

        {/* Section 2: REVENUE & DEAL MIX */}
        <section aria-labelledby="section-revenue">
          {SECTION_HEADER('section-revenue', 'Revenue & Deal Mix')}
          <div
            data-cro-tile-grid
            className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3"
          >
            <div className="sm:col-span-2 lg:col-span-3">
              <CroArrPacingTile
                data={cro01.data}
                isLoading={cro01.isLoading}
                isError={cro01.isError}
                error={cro01.error}
                refetch={() => { void cro01.refetch(); }}
              />
            </div>
            <CroTile
              metricId="CRO-08"
              data={cro08.data}
              isLoading={cro08.isLoading}
              isError={cro08.isError}
              error={cro08.error}
              refetch={() => { void cro08.refetch(); }}
            />
            <CroTile
              metricId="CRO-09"
              data={cro09.data}
              isLoading={cro09.isLoading}
              isError={cro09.isError}
              error={cro09.error}
              refetch={() => { void cro09.refetch(); }}
            />
            {/* MAIN-12 Avg $ / Closed-Won Deal — ported from main dashboard. */}
            <div className="sm:col-span-2 lg:col-span-3">
              {renderMainBucketed('MAIN-12', 'lineByQuarter')}
            </div>
          </div>
        </section>

        {/* Section 3: FORECAST — CRO-10 only (CRO-11 variance removed 2026-07-10) */}
        <section aria-labelledby="section-forecast">
          {SECTION_HEADER('section-forecast', 'Forecast')}
          <div
            data-cro-tile-grid
            className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2"
          >
            <CroTile
              metricId="CRO-10"
              data={cro10.data}
              isLoading={cro10.isLoading}
              isError={cro10.isError}
              error={cro10.error}
              refetch={() => { void cro10.refetch(); }}
            />
          </div>
        </section>

        {/* Section 4: AE ACTIVITY */}
        <section aria-labelledby="section-ae-activity">
          {SECTION_HEADER('section-ae-activity', 'AE Activity')}
          <div
            data-cro-tile-grid
            className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2"
          >
            <CroTrendTile
              metricId="CRO-06"
              data={cro06.data}
              isLoading={cro06.isLoading}
              isError={cro06.isError}
              error={cro06.error}
              refetch={() => { void cro06.refetch(); }}
            />
            <CroTrendTile
              metricId="CRO-07"
              data={cro07.data}
              isLoading={cro07.isLoading}
              isError={cro07.isError}
              error={cro07.error}
              refetch={() => { void cro07.refetch(); }}
            />
          </div>

          {/* MAIN-04 Outbound Activity — ported from main dashboard, full-width. */}
          <div className="mt-3">
            {renderMainBucketed('MAIN-04', 'weeklyBar')}
          </div>
        </section>
      </div>
    </AppShell>
  );
}
